import React, {
  useCallback, useEffect, useRef, useState,
} from "react"
import Formsy from "formsy-react"
import types from "prop-types"
import clsx from "clsx"
import { v4 as makeUuid } from "uuid"
import { useSubmit } from "hooks"
import { Button, PrimaryButton } from "shared/buttons"
import { FormsyHiddenInput } from "shared/inputs"
import AnswerOptionsListFormFields from "./AnswerOptionsListFormFields"

const buildOption = (value = "") => ({ id: makeUuid(), value })

const AnswerOptionsListForm = ({
  action,
  className = "",
  edit = false,
  initialName = "",
  initialOptions = [],
  listNames,
  onCancel,
  ref = undefined,
}) => {
  const { canSubmit, enableSubmit, disableSubmit } = useSubmit(false)
  const [submitting, setSubmitting] = useState(false)
  const [options, setOptions] = useState(
    initialOptions.length ? initialOptions.map(buildOption) : [buildOption()],
  )
  const optionInputRefs = useRef([])
  const shouldFocusNewOption = useRef(false)

  const getOptionInputRef = useCallback((index) => (el) => {
    optionInputRefs.current[index] = el
  }, [])

  const addOption = useCallback(() => {
    shouldFocusNewOption.current = true
    setOptions((prev) => [...prev, buildOption()])
  }, [])

  useEffect(() => {
    if (shouldFocusNewOption.current) {
      shouldFocusNewOption.current = false
      optionInputRefs.current[options.length - 1]?.focus()
    }
  }, [options.length])

  const removeOption = useCallback((index) => () => setOptions((prev) => {
    const next = [...prev]
    next.splice(index, 1)
    return next
  }), [])

  const updateOption = useCallback((index) => (value) => setOptions((prev) => {
    const next = [...prev]
    next[index] = { ...next[index], value }
    return next
  }), [])

  const moveOption = useCallback(({ destination, source }) => {
    if (!destination) return

    setOptions((prev) => {
      const next = [...prev]
      const [movedOption] = next.splice(source.index, 1)
      next.splice(destination.index, 0, movedOption)
      return next
    })
  }, [])

  const importOptions = useCallback((values) => {
    setOptions(values.map(buildOption))
  }, [])

  const canSubmitForm = canSubmit && options.length > 0

  return (
    <Formsy
      action={action}
      className={clsx(className)}
      method="post"
      onInvalid={disableSubmit}
      onSubmit={() => setSubmitting(true)}
      onValid={enableSubmit}
      preventDefaultSubmit={false}
    >
      { edit && <FormsyHiddenInput name="_method" value="patch" /> }
      <AnswerOptionsListFormFields
        addOption={addOption}
        className={canSubmitForm ? "mb-8" : "mb-16"}
        getOptionInputRef={getOptionInputRef}
        initialName={initialName}
        listNames={listNames}
        moveOption={moveOption}
        nameInputRef={ref}
        onImportOptions={importOptions}
        options={options}
        removeOption={removeOption}
        updateOption={updateOption}
      />
      <div className="flex justify-center">
        <Button className="bg-white border border-light mr-4" text="Cancel" onClick={onCancel} ariaLabel="cancel" />
        <PrimaryButton
          ariaLabel="Add Answer Options List"
          disabled={!canSubmitForm || submitting}
          text="Save"
          type="submit"
        />
      </div>
    </Formsy>
  )
}

AnswerOptionsListForm.propTypes = {
  action: types.string.isRequired,
  className: types.string,
  edit: types.bool,
  initialName: types.string,
  initialOptions: types.arrayOf(types.string),
  listNames: types.arrayOf(types.string).isRequired,
  onCancel: types.func.isRequired,
  ref: types.oneOfType([types.func, types.object]),
}

export default AnswerOptionsListForm
